import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  /** Cancel button action. Defaults to `onClose` (X / overlay still always call `onClose`). */
  onCancel?: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  variant?: "default" | "destructive";
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  isLoading = false,
  variant = "default",
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    void onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent className="box-border w-[min(100vw-2rem,32rem)] max-w-lg overflow-hidden">
        <DialogHeader className="min-w-0 space-y-2 pr-6">
          <DialogTitle className="text-[var(--lm-foreground-strong)] dark:text-slate-100">{title}</DialogTitle>
          <DialogDescription className="break-words text-[var(--lm-muted)] dark:text-slate-300/85">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 w-full min-w-0">
          <Button
            type="button"
            variant="outline"
            className="w-full shrink-0 sm:w-auto"
            onClick={onCancel ?? onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === "destructive" ? "destructive" : "default"}
            className="w-full shrink-0 sm:w-auto"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Please wait…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
